import * as amplitude from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import * as sessionReplay from '@amplitude/session-replay-browser';
import { hasProperty } from '@squaredup/utilities';
import { debounce } from 'lodash';
import { clarity } from 'react-microsoft-clarity';
import Auth from 'services/Auth';
import { v4 as uuidv4 } from 'uuid';
import { environment, isProd, isTestEnvironment } from './environment';

// Simple utility function to ensure that any issues with sending those events doesn't take down our code!
export default function trackEvent(eventName: string, payload: any) {
    try {
        const context: any = Auth?.user?.tenant ? { groupId: Auth.user.tenant } : {};
        if (hasProperty(window, 'analytics') && typeof window.analytics === 'object' && window.analytics != null) {
            window.analytics.track(eventName, payload, context); //eslint-disable-line no-undef
        }
    } catch (e) {
        // Never expect to hit this, it would indicate we're mis configured, or someone has messed
        // with index.html, around how we init segment
        console.error('Analytics error', e); //eslint-disable-line no-console
    }
}

export const trackEventDebounced = debounce((eventName, payload) => {
    trackEvent(eventName, payload);
}, 1000);

export const identifyUser = (tenantName: string, tenantStage: string) => {
    if (hasProperty(window, 'analytics') && typeof window.analytics === 'object' && window.analytics != null) {
        if (Auth.user?.id) {
            window.analytics.identify(Auth.user.id, {
                email: Auth.user.name,
                name: Auth.user.name,
                username: Auth.user.name
            });
        }

        if (Auth.user?.tenant) {
            window.analytics.group(Auth.user.tenant, {
                name: tenantName,
                activeStage: tenantStage
            });
        }
    }
};

const urlParams = new URLSearchParams(window.location.search);

export const initClarity = (tenantId: string, tenantName: string, tenantStage: string, tierTemplate: string) => {
    if ((isProd && !isTestEnvironment && tierTemplate === 'free') || urlParams.get('analytics') === 'enabled') {
        if (!clarity.hasStarted()) {
            clarity.init('nfzf2bkoi4');
            const email = Auth.user?.name ?? '<no user name>';
            clarity.identify(email, {});
            clarity.setTag('userId', Auth.user?.id ?? '<no user id>');
            clarity.setTag('userEmail', email);
            clarity.setTag('tenantName', tenantName);
            clarity.setTag('tenant', tenantId);
            clarity.setTag('stage', tenantStage);
        }
    }
};

export const initAmplitude = async (
    tenantId: string,
    tenantName: string,
    tenantStage: string,
    tierTemplate: string
) => {
    const enableClient = (isProd && !isTestEnvironment) || urlParams.get('analytics') === 'enabled';
    const enableReplay = tierTemplate === 'free' || urlParams.get('analytics') === 'enabled';
    const apiKey = isProd ? '352b152dee41e0c885c8ed5d6bf18f56' : 'd7744e19363da5203cc22e4c8bc7211c';

    function getSessionIdCookie(): number {
        const match = document.cookie.match('(^|;)\\s*analytics_session_id=(\\d+)(?=;|$)')?.pop();
        return match ? parseInt(match) : 0;
    }

    function setSessionIdCookie(sessionId: number) {
        document.cookie = `analytics_session_id=${sessionId};`;
    }

    if (enableClient) {
        amplitude.add(engagementPlugin());
        amplitude.init(apiKey, Auth.user?.id, {
            serverZone: 'EU',
            flushQueueSize: 1,
            autocapture: {
                attribution: true,
                elementInteractions: true,
                fileDownloads: true,
                formInteractions: true,
                pageViews: true,
                sessions: true
            }
        });

        if (enableReplay) {
            await sessionReplay.init(apiKey, {
                serverZone: 'EU',
                deviceId: window.analytics?.user?.().anonymousId?.() ?? uuidv4(),
                sessionId: getSessionIdCookie()
            }).promise;

            window.analytics.addSourceMiddleware(async ({ payload, next }) => {
                const storedSessionId = getSessionIdCookie();
                const nextSessionId =
                    (payload.obj.integrations?.['Actions Amplitude'] as { session_id?: number })?.session_id || 0;
                if (storedSessionId < nextSessionId) {
                    setSessionIdCookie(nextSessionId);
                    await sessionReplay.setSessionId(nextSessionId).promise;
                }
                next(payload);
            });

            window.analytics.addSourceMiddleware(({ payload, next }) => {
                const sessionReplayProperties = sessionReplay.getSessionReplayProperties();
                if (payload.type() === 'track') {
                    payload.obj.properties = {
                        ...payload.obj.properties,
                        ...sessionReplayProperties
                    };
                }
                next(payload);
            });
        }

        // add the user to a group per trait to allow for filtering
        // i.e. all users in ten-abc, all users on stage-123, all users on free tier, etc.
        amplitude.setGroup('id', tenantId);
        amplitude.setGroup('stage', tenantStage);
        amplitude.setGroup('tier', tierTemplate);
        amplitude.setGroup('environment', environment);

        // enrich the tenant group with its traits
        const groupEvent = new amplitude.Identify();
        groupEvent.set('name', tenantName);
        groupEvent.set('stage', tenantStage);
        groupEvent.set('tier', tierTemplate);
        groupEvent.set('environment', environment);
        amplitude.groupIdentify('id', tenantId, groupEvent);

        if (Auth.user?.name) {
            const identifyEvent = new amplitude.Identify();
            identifyEvent.set('email', Auth.user.name);
            amplitude.identify(identifyEvent);
        }
    }
};
